import { defineComponent, h, nextTick } from 'vue';
import BaseComponent from '../BaseComponentMixin.jsx';
import methods from '../../../Shared/methods';
import utils from '../../../Shared/utils.jsx';
import { QSelect, QItem, QItemSection, QItemLabel, QIcon, QSeparator } from 'quasar';

import TranslationContainer from './../TranslationContainer.jsx';

export default defineComponent({
    name: 'DropdownMultiSelectBase',
    emits: ['input', 'empty'],
    components: {
        TranslationContainer,
        QSelect,
        QItem,
        QItemSection,
        QItemLabel,
        QIcon,
        QSeparator,
    },
    data: function () {
        return {
            uniqueref: null,
            emptySelected: false,
            filter: '',
        }
    },
    props: {
        source: {
            type: Array,
            default: () => []
        },
        ccmodel: null,
        nameField: null,
        valueField: null,
        placeholder: {
            type: String,
            default: ''
        },
        selectionLimit: {
            type: Number,
            default: 0
        },
        showCheckAll: {
            type: Boolean,
            default: true
        },
        showUncheckAll: {
            type: Boolean,
            default: true
        },
        dynamicButtonText: {
            type: Boolean,
            default: false
        },
        dynamicItemCount: {
            type: Number,
            default: 0
        },
        hint: {
            type: String,
            default: ''
        },
        hideDetails: {
            type: Boolean,
            default: true
        },
        includeEmpty: {
            type: Boolean,
            default: false,
        },
        emptyLabel: {
            type: String,
            default: '',
        },
        initialEmptyValue: {
            type: Boolean,
            default: false,
        },
        error: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: '',
        },
    },
    created() {
        this.uniqueref = utils.generateUUID();
        this.emptySelected = this.initialEmptyValue;
    },
    computed: {
        selectionLimitNumber() {
            const limit = Number(this.selectionLimit);
            return Number.isFinite(limit) ? limit : 0;
        },
        localModel() {
            if (Array.isArray(this.ccmodel))
                return this.ccmodel;
            if (this.ccmodel === undefined || this.ccmodel === null || this.ccmodel === '')
                return [];
            return [this.ccmodel];
        },
        // How many of the selected values are items in the list. A saved value whose item has since
        // gone (a renamed or removed source) has no checkbox to show or clear, so it is not counted.
        modelLength() {
            if (!this.hasSourceData)
                return this.localModel.length;
            const seen = new Set();
            for (const v of this.localModel) {
                let i = this.allOptions.findIndex(o => this.valuesEqual(o._value, v));
                if (i < 0 && v != null)
                    i = this.allOptions.findIndex(o => String(o._label) === String(v));
                if (i >= 0)
                    seen.add(i);
            }
            return seen.size;
        },
        normalizedModelValue() {
            if (this.selectionLimitNumber === 1) {
                const raw = Array.isArray(this.ccmodel) ? this.ccmodel[0] : this.ccmodel;
                if (raw === undefined || raw === null || raw === '')
                    return raw;
                return this.normalizeModelValues([raw])[0];
            }
            return this.normalizeModelValues(this.localModel);
        },
        allSelected() {
            const selectableCount = this.optionsNormalized.filter(o => !o._disabled).length;
            return (this.modelLength === selectableCount && (!this.includeEmpty || this.emptySelected)) ||
                (this.selectionLimitNumber > 0 && this.modelLength === this.selectionLimitNumber);
        },
        someSelected() {
            return (this.modelLength > 0 || this.emptySelected) && !this.allSelected;
        },
        selectAllIcon() {
            if (this.allSelected) return 'mdi-close-box'
            if (this.someSelected) return 'mdi-minus-box'
            return 'mdi-checkbox-blank-outline'
        },
        selectedDisplayText() {
            const labels = this.localModel.map((val) => {
                const opt = this.findOptionByValue(val);
                return opt ? opt._label : String(val);
            });
            if (this.emptySelected && this.includeEmpty)
                labels.unshift(this.emptyLabel || '[ Blank ]');
            return labels.join(', ') || '';
        },
        hasSourceData() {
            return (this.source || []).length > 0;
        },
        hasHint() {
            return this.hint != null && String(this.hint).trim() !== '';
        },
        allOptions() {
            return (this.source || []).map((item) => {
                const props = this.getItemProps(item);
                return { ...item, _label: props.title ?? '', _value: props.value, _disabled: props.disabled };
            });
        },
        optionsNormalized() {
            const items = this.allOptions;

            if (this.filter)
                return items.filter(a => a._label.toLowerCase().includes(this.filter.toLowerCase()));
            else
                return items;
        },
    },
    //Created Replaced with preRenderComplete
    methods: {
        ...methods,
        valuesEqual(a, b) {
            if (a === b)
                return true;
            if (a == null || b == null)
                return false;
            if (typeof a === 'object' && typeof b === 'object') {
                try {
                    return JSON.stringify(a) === JSON.stringify(b);
                }
                catch {
                    return false;
                }
            }
            return String(a) === String(b);
        },
        valueKey(v) {
            if (v == null)
                return v;
            if (typeof v === 'object') {
                try {
                    return JSON.stringify(v);
                }
                catch {
                    return v;
                }
            }
            return String(v);
        },
        findOptionByValue(val) {
            return this.optionsNormalized.find(o => this.valuesEqual(o._value, val));
        },
        normalizeModelValues(values) {
            if (!Array.isArray(values))
                return values;

            const seen = new Set();
            const result = [];

            for (const v of values) {
                let opt = this.findOptionByValue(v);

                if (!opt && v != null) {
                    opt = this.optionsNormalized.find(o =>
                        o._label === v || String(o._label) === String(v)
                    );
                }

                const canonical = opt ? opt._value : v;
                const key = this.valueKey(canonical);

                if (!seen.has(key)) {
                    seen.add(key);
                    result.push(canonical);
                }
            }

            return result;
        },
        async onInput(item) {
            let value = null;
            const selectionLimit = this.selectionLimitNumber;
            // A selection limit of 0 is meant to mean unlimited length, and a selection limit of 1 will emit a 
            // single item. We want to pop the last item off of the array if it violates the selection limit 
            if (Array.isArray(item) && selectionLimit != 0 && item.length > selectionLimit)
                item.pop();
            if (!Array.isArray(item))
                value = [item];
            else
                value = item;

            if (!value)
                value = [];

            value = this.normalizeModelValues(value);

            if (Array.isArray(value) && value.length == 0 && this.emptySelected)
                this.emptySelected = false;

            this.$emit('input', value);
        },
        filterFn(val, update) {
            update(() => this.filter = val);
        },
        async toggleAll() {
            nextTick(() => {
                let value = null;
                if (this.allSelected) {
                    value = [];
                    this.emptySelected = false;
                }
                else {
                    const opts = this.optionsNormalized.filter(o => !o._disabled);
                    value = opts.map(x => x._value).slice(0, this.selectionLimitNumber > 0 ? this.selectionLimitNumber : undefined);
                    this.emptySelected = this.includeEmpty;
                }
                this.$emit('input', value);
                this.$emit('empty', this.emptySelected);
            });
        },
        getItemProps(item) {
            return {
                title: typeof this.nameField === 'function' ? this.nameField(item) : item[this.nameField], // item.text,
                value: typeof this.valueField === 'function' ? this.valueField(item) : item[this.valueField], // item.value,
                disabled: item.disabled,
            };
        },
    },
    render() {
        try {
            const prepend = [];

            if (this.hasSourceData && this.selectionLimitNumber !== 1 && (this.showCheckAll || this.showUncheckAll)) {
                prepend.push(
                    <QItem key="toggle-all" clickable onClick={this.toggleAll}>
                        <QItemSection avatar>
                            <QIcon name={this.selectAllIcon} color={this.modelLength > 0 ? 'primary' : undefined} />
                        </QItemSection>
                        <QItemSection>
                            <QItemLabel>
                                {this.allSelected ? <TranslationContainer context={this} value="Unselect all" /> : <TranslationContainer context={this} value="Select all" />}
                            </QItemLabel>
                        </QItemSection>
                    </QItem>,
                    <QSeparator key="sep-toggle" class="q-mt-sm" />
                );
            }

            if (this.includeEmpty) {
                prepend.push(
                    <QItem
                        key="empty-option"
                        clickable
                        disable={this.modelLength === 0}
                        active={this.emptySelected}
                        active-class="text-primary"
                        onClick={() => { this.emptySelected = !this.emptySelected; this.$emit('empty', this.emptySelected); }}
                    >
                        <QItemSection avatar>
                            <QIcon
                                name={this.emptySelected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'}
                                color={this.modelLength > 0 ? 'primary' : undefined}
                            />
                        </QItemSection>
                        <QItemSection>
                            <QItemLabel>
                                <TranslationContainer context={this} value={this.emptyLabel || '[ Blank ]'} />
                            </QItemLabel>
                        </QItemSection>
                    </QItem>
                );
            }

            const isSingleSelect = this.selectionLimitNumber === 1;
            const modelValue = this.normalizedModelValue;
            const slots = {};
            if (this.$slots?.label)
                slots.label = () => this.$slots.label();
            if (prepend.length)
                slots['before-options'] = () => prepend;
            slots['no-option'] = () => (
                <QItem disable>
                    <QItemSection>
                        <QItemLabel>
                            <TranslationContainer context={this} value="No data available" />
                        </QItemLabel>
                    </QItemSection>
                </QItem>
            );
            if (!isSingleSelect) {
                slots.option = (scope) => (
                    <QItem key={scope.index} {...scope.itemProps}>
                        <QItemSection avatar>
                            <QIcon
                                name={scope.selected ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'}
                                color={scope.selected ? 'primary' : undefined}
                            />
                        </QItemSection>
                        <QItemSection>
                            <QItemLabel>{scope.label}</QItemLabel>
                        </QItemSection>
                    </QItem>
                );
            }
            // Custom selection display: only render once (index 0) to avoid duplicate name/value chips
            slots['selected-item'] = (scope) => {
                const selectedCount = this.modelLength + (this.emptySelected && this.includeEmpty ? 1 : 0);
                const dynamicItemCount = Number(this.dynamicItemCount) || 0;

                if (this.dynamicButtonText && typeof scope.opt?._label === 'string') {
                    const isLastIndex = scope.index + 1 === this.modelLength || scope.index + 1 === dynamicItemCount;
                    const extraCount = Math.max(this.modelLength - dynamicItemCount, 0) + (this.emptySelected && this.includeEmpty ? 1 : 0);

                    if (scope.index < dynamicItemCount) {
                        return (
                            <span class="ellipsis">
                                {scope.index === 0 ? null : <span>, </span>}
                                {scope.opt._label}
                                {extraCount > 0 && isLastIndex ? <span>, +{extraCount} <TranslationContainer context={this} value="selected" /> </span> : null}
                            </span>
                        );
                    }

                    return null;
                }

                if (scope.index === 0 && selectedCount) {
                    return (
                        <span class="ellipsis">
                            {selectedCount} <TranslationContainer context={this} value="selected" />
                        </span>
                    );
                }

                return null;
            };

            const hasLabelSlot = !!this.$slots?.label;
            const hasLabel = hasLabelSlot || !!this.label;

            return (
                <QSelect
                    ref={this.uniqueref}
                    bg-color="white"
                    outlined
                    dense
                    label={hasLabelSlot ? undefined : (this.label || undefined)}
                    label-slot={hasLabelSlot}
                    hide-bottom-space={this.hideDetails}
                    bottom-slots={!this.hideDetails}
                    hide-hint={!this.hasHint}
                    use-input
                    input-debounce={0}
                    popup-content-class="q-select-max-popup-height"

                    use-chips={false}
                    options={this.optionsNormalized}
                    option-label="_label"
                    option-value="_value"
                    option-disable={(opt) => !!opt._disabled}
                    map-options
                    emit-value
                    multiple={!isSingleSelect}
                    modelValue={modelValue}
                    onUpdate:modelValue={(val) => this.onInput(val)}
                    onFilter={this.filterFn}
                    placeholder={hasLabel ? undefined : this.placeholder}
                    hint={this.hasHint ? this.hint : undefined}
                    persistent-hint={this.hasHint}
                    autocomplete="off"
                    error={this.error}
                >
                    {slots}
                </QSelect>
            );
        }
        catch (e) {
            utils.error('DropdownMultiSelectBase Render failed', e);
            return <div>DropdownMultiSelectBase Failed to Render {e}</div>;
        }
    }
});

